import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { HoldToConfirmButton } from "@src/appV2/redesign/components/HoldToConfirmButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { type PlacementDetail } from "../api/useGetPlacement";

interface ChildcareSupportChatBottomSheetProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  hasMissingOrPendingDocuments: boolean;
  isLoading: boolean;
  onClose: () => void;
}

export function ChildcareSupportChatBottomSheet(props: ChildcareSupportChatBottomSheetProps) {
  const {
    modalState,
    placementDetails,
    placementCandidate,
    hasMissingOrPendingDocuments,
    isLoading,
    onClose,
  } = props;
  const { userId: workerId } = useDefinedWorker();

  const eventContext = {
    workerId,
    placementId: placementDetails.id,
    placementCandidateId: placementCandidate.id,
  };

  useLogEffect(APP_V2_APP_EVENTS.CHILDCARE_SUPPORT_CHAT_MODAL_VIEWED, eventContext);

  const handleClose = () => {
    modalState.closeModal();
    onClose();
  };

  const handleDismiss = () => {
    logEvent(APP_V2_APP_EVENTS.CHILDCARE_SUPPORT_CHAT_EXITED, eventContext);
    modalState.closeModal();
  };

  if (!hasMissingOrPendingDocuments) {
    handleClose();
    return null;
  }

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <HoldToConfirmButton
            fullWidth
            size="large"
            confirmDurationInMs={1500}
            isLoading={isLoading}
            disabled={isLoading}
            onConfirm={() => {
              logEvent(APP_V2_APP_EVENTS.CHILDCARE_SUPPORT_CHAT_MODAL_OPEN_TAPPED, eventContext);
              void openZendeskMessaging();
            }}
          >
            Open
          </HoldToConfirmButton>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={handleDismiss} />}
    >
      <Box sx={{ px: 5, py: 6 }}>
        <Stack spacing={7} alignItems="center" justifyContent="center">
          <Illustration type="document-upload" />

          <Text variant="h3" textAlign="center">
            Interview Booking Pending
          </Text>

          <Stack spacing={2}>
            <Text color={(theme) => theme.palette.text.secondary} textAlign="center">
              A chat conversation will appear to continue.
            </Text>
            <Text bold color={(theme) => theme.palette.text.secondary} textAlign="center">
              If it does not appear, tap &quot;New conversation&quot; at the bottom of the screen.
            </Text>
          </Stack>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
